import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import {
  createLocalizedPath,
  getInitialLanguage,
  translations
} from '@/shared/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call optional onError callback
    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Here you could also log the error to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = createLocalizedPath('/', getInitialLanguage());
  };

  render() {
    if (this.state.hasError) {
      const errorCopy = translations[getInitialLanguage()].errorBoundary;

      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-background-primary flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-background-secondary rounded-lg border border-border-primary p-6 text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-accent-error" />
            </div>

            <h1 className="text-xl font-bold text-text-primary mb-2">
              {errorCopy.title}
            </h1>

            <p className="text-text-secondary mb-6">{errorCopy.description}</p>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
                  {errorCopy.detailsSummary}
                </summary>
                <pre className="mt-2 p-3 bg-background-tertiary rounded text-xs overflow-auto text-accent-error">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-accent-primary hover:bg-accent-secondary text-text-inverse rounded-lg font-medium transition-colors duration-200 shadow-md cursor-pointer"
              >
                <RefreshCw className="h-5 w-5" />
                <span>{errorCopy.tryAgain}</span>
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={this.handleRefresh}
                  className="flex items-center justify-center gap-3 px-4 py-3 bg-background-tertiary hover:bg-background-hover text-text-primary border border-border-primary rounded-lg font-medium transition-all duration-200 cursor-pointer"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>{errorCopy.reloadPage}</span>
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center gap-3 px-4 py-3 bg-background-tertiary hover:bg-background-hover text-text-primary border border-border-primary rounded-lg font-medium transition-all duration-200 cursor-pointer"
                >
                  <Home className="h-4 w-4" />
                  <span>{errorCopy.goHome}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
}

export default ErrorBoundary;
